import { UserCoreInterface, UserInterfaceDto } from './shared/interfaces/user-interface';
import { DomainInterface, DomainProductDto } from './shared/interfaces/domain-interface';
import { DomainsService } from './shared/services/domains.service';
import { LoadingService } from './core/services/loading.service';
import { Component, computed, DestroyRef, HostListener, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router, Event, RouterOutlet } from '@angular/router';
import { catchError, filter, skipWhile, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { DomainUpdateStatus } from './shared/enums/enumerations';
import { DomainUpdateService } from './shared/services/domain-update.service';
import {
  DomainUpdateEventResultInterface,
  PlatformOrigin,
} from './shared/interfaces/domainUpdateEventResult-interface';
import { CurrentUserService } from './shared/services/current-user.service';
import {
  HxApplicationModule,
  HxNavigationModule,
  HxPageModule,
  HxSpinnerComponent,
  NavItem,
} from '@hubbix/common-components';
import { DASHBOARD_ROUTE_PATH } from './shared/constants/app-routes.constant';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { AuthenticationService } from '@core/authentication/services/authentication.service';
import { UserInfoService } from '@shared/services/user.service';
import { InformationsComponent } from '@shared/components/informations/informations.component';
import { ShineMigrationService } from '@shared/services/shine-migration.service';
import { ShineMigrationType } from '@shared/enums/shine-migration-type.enum';

interface LegalNotice {
  title: string;
  name: string;
  subMenus: Array<LegalNoticeSubMenus>;
}

interface LegalNoticeSubMenus {
  title: string;
  externalLink?: boolean;
  link?: string;
}

export enum MigrationBannerType {
  OL = 'OL',
  SHINE_BATIMENT = 'SHINE_BATIMENT',
  SHINE_GC = 'SHINE_GC',
  SHINE_DUO = 'SHINE_DUO',
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    HxNavigationModule,
    HxApplicationModule,
    HxSpinnerComponent,
    HxPageModule,
    RouterOutlet,
    NgClass,
    InformationsComponent,
  ],
})
export class AppComponent implements OnInit {
  public currentDomain$!: Observable<DomainInterface | null>;

  public user: UserCoreInterface | undefined;

  public title: string = 'ebp-gestion-domain';

  public loading$!: Observable<boolean>;

  public domainUpdateEvents!: Array<DomainUpdateEventResultInterface>;

  public blockedProducts: Array<DomainProductDto> = [];

  public isAuthorized: boolean = false;

  protected readonly navItems!: Signal<Array<NavItem>>;

  protected readonly applicationBackgroundClass: Signal<string> = computed(() => {
    const routeData: Data = this.activatedRouteData();

    return routeData['applicationBackgroundClass'];
  });

  protected readonly legalNotice: LegalNotice = {
    title: 'Mentions légales',
    name: 'legal-mention',
    subMenus: [
      {
        title: 'CGV',
        externalLink: true,
        link: 'https://www.ebp.com/cgv',
      },
      {
        title: 'Mentions légales',
        link: 'https://www.ebp.com/mentions-legales',
        externalLink: true,
      },
      {
        title: 'Politique de confidentialité',
        link: 'https://www.ebp.com/politique-de-confidentialite',
        externalLink: true,
      },
      {
        title: "Configuration d'utilisation",
        externalLink: true,
        link: 'https://www.ebp.com/configurations-utilisation',
      },
      {
        title: `Version du logiciel: 'v1.0.0'`,
      },
    ],
  };

  public migrationBannerType: MigrationBannerType | undefined = MigrationBannerType.SHINE_BATIMENT;

  public shineMigrationType?: ShineMigrationType;

  public domainId: string = '';

  private previousUrl: string = '';

  public constructor(
    public readonly authenticationService: AuthenticationService,
    private readonly loader: LoadingService,
    private readonly router: Router,
    private readonly domainsService: DomainsService,
    private readonly domainUpdateService: DomainUpdateService,
    private readonly currentUserService: CurrentUserService,
    private readonly userInfoService: UserInfoService,
    private readonly destroyRef: DestroyRef,
    private readonly shineMigrationService: ShineMigrationService,
  ) {
    this.navItems = this.createNavItems();
  }

  public ngOnInit(): void {
    if (this.authenticationService.isAuthenticated()) {
      this.userInfoService
        .getUserInfo()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((user: UserCoreInterface) => {
          this.user = user;
        });
    }

    this.currentDomain$ = this.domainsService.currentDomain$;

    if (this.authenticationService.isAuthenticated()) {
      // Get the current domain
      this.currentDomain$
        ?.pipe(
          takeUntilDestroyed(this.destroyRef),
          skipWhile((domain: DomainInterface | null) => !domain),
        )
        .subscribe((currentDomain: DomainInterface | null) => {
          this.domainId = currentDomain?.id ?? '';
          if (currentDomain) {
            this.getShineMigrationType(currentDomain.id);

            this.domainsService
              .getUserConnectedInfos(currentDomain?.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((user: UserInterfaceDto) => {
                this.currentUserService.setCurrentUserData(user);
                this.isAuthorized = Object.values(user.roles).some(
                  (role) => role === 'Administrator' || role === 'ChartAccountant',
                );
              });
          }
        });
      setInterval(() => {
        this.domainUpdateEvents = sessionStorage.getItem('LastEvent')
          ? (this.domainUpdateEvents = JSON.parse(sessionStorage.getItem('LastEvent') || ''))
          : [];
      }, 5000);
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.removeClassFromPreviousRoute(this.previousUrl);
        this.addClassToCurrentRoute(event.url);
        this.previousUrl = event.url;
      }
    });

    this.loading$ = this.loader.loading$;
  }

  eventNotificationRefreshing(id: string) {
    this.domainUpdateService
      .getDomainUpdateEventsByStatus(id, DomainUpdateStatus.Pending, PlatformOrigin.Commercial)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((DomainUpdateEventResult: Array<DomainUpdateEventResultInterface>) => {
        if (DomainUpdateEventResult) {
          sessionStorage.setItem('LastEvent', JSON.stringify(DomainUpdateEventResult));
          this.domainUpdateEvents = DomainUpdateEventResult;
        }
      });
  }

  @HostListener('window:unload', ['$event'])
  unloadHandler() {
    this.domainsService.clearLocalStorageDomain();
  }

  public addClassToCurrentRoute(url: string): void {
    const elem: Element | null = this.getElemByUrl(url);

    elem?.classList.add('is-active');
  }

  public removeClassFromPreviousRoute(url: string): void {
    const elem: Element | null = this.getElemByUrl(url);

    elem?.classList.remove('is-active');
  }

  public getElemByUrl(url: string): Element | null {
    let elem!: Element | null;

    switch (url) {
      case '/entreprise':
        elem = document.querySelector('[ebp-ta="layout-menu-customer"]');
        break;

      case '/utilisateurs':
        elem = document.querySelector('[ebp-ta="layout-menu-users-menu"]');
        break;
    }

    return elem;
  }

  private createNavItems(): Signal<Array<NavItem>> {
    return computed(() => {
      const items: Array<NavItem> = [];

      items.push(...this.addCommonNavItems());

      return items;
    });
  }

  private addCommonNavItems(): Array<NavItem> {
    return [
      {
        title: 'Dashboard',
        link: DASHBOARD_ROUTE_PATH,
        name: 'dashboard',
        icon: 'dashboard',
      },
      {
        title: 'Entreprise',
        name: 'customer',
        icon: 'business',
      },
      {
        title: 'Utilisateurs',
        name: 'users-menu',
        icon: 'group',
      },
    ];
  }

  private readonly activatedRouteData: Signal<Data> = toSignal<Data, Data>(this.getActivatedRouteData(), {
    initialValue: {},
  });

  private getActivatedRouteData(): Observable<Data> {
    return this.router.events.pipe(
      filter<Event, NavigationEnd>((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
      switchMap(() => {
        let route: ActivatedRoute = this.router.routerState.root;

        while (route.firstChild) {
          route = route.firstChild;
        }

        return route.data;
      }),
    );
  }

  private getShineMigrationType(domainId: string): void {
    this.shineMigrationService
      .getShineMigration(domainId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        const shineMigrationType: ShineMigrationType | undefined = response?.shineMigrationType;

        if (shineMigrationType === undefined || shineMigrationType === ShineMigrationType.None) {
          this.shineMigrationType = undefined;
          return;
        }

        this.shineMigrationType = shineMigrationType;
      });
  }
}
